import { FormattedMessage, useIntl } from 'react-intl';
import { Form } from 'antd';

import { Helmet } from 'react-helmet';
import { useEffect } from 'react';
import { PageProps } from '@/types/PageProps';

import { SaveButton, TabContentWrapper } from '@/components/_common';

import { useRouteMatchParam } from '@/hooks';
import { usePartMutation } from '@/core/domain/operational/Parts/Parts.hooks';
import { usePurchase } from '@/core/domain/purchase/Purchase.hooks';
import PurchaseItemsFormTabs from '@/components/Movements/Purchases/PurchaseDetails/PurchaseItemsPurchase/PurchaseItemsFormTabs';
import {
  CreatePurchaseItemRequest,
  PurchaseItemsFormValues,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { usePurchaseItemCreate } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { useHistory } from 'react-router-dom';
import { useConsumableMutation } from '@/core/domain/operational/Consumables/Consumables.hooks';
import PurchaseItemsButtonsRepeated from '@/components/Movements/Purchases/PurchaseDetails/PurchaseItemsPurchase/PurchaseItemsButtonsRepeated';
import { PurchaseItemType } from '@/core/domain/purchase/Purchase.types';
import { IcmsOriginIndex } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';

function PurchaseCreateItemPurchase({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const [form] = Form.useForm<PurchaseItemsFormValues>();

  const partId = +useRouteMatchParam('partId');
  const consumableId = +useRouteMatchParam('consumableId');
  const purchaseId = +useRouteMatchParam('purchaseId');

  const { data: part, mutateAsync: partMutateAsync } = usePartMutation();
  const { data: consumable, mutateAsync: consumableMutateAsync } =
    useConsumableMutation();

  useEffect(() => {
    if (partId) {
      partMutateAsync(partId);
    } else if (consumableId) {
      consumableMutateAsync(consumableId);
    }
  }, [partId, consumableId]);

  const { data: purchase } = usePurchase(purchaseId);

  const createItemMutation = usePurchaseItemCreate();

  useEffect(() => {
    if (part) {
      form.setFieldsValue({
        cfopId: part.cfopId,
        name: part.name,
        manufacturerId: part.manufacturer?.id,
        ncmId: part.ncmId,
        unitMeasurement: part?.unit,
        purchaseItemTaxes: {
          originIcms: IcmsOriginIndex[part?.icmsOrigin],
        },
      });
    } else if (consumable) {
      form.setFieldsValue({
        cfopId: consumable.cfopId,
        name: consumable.name,
        manufacturerId: consumable.manufacturer.id,
        ncmId: consumable.ncmId,
        unitMeasurement: consumable.unit,
      });
    }
  }, [part, consumable]);

  const handleCreateItemPurchase = async () => {
    try {
      const values = await form.validateFields();

      const obj: CreatePurchaseItemRequest = {
        ...values,
        purchase: purchaseId,
        partId: partId ?? null,
        consumableId: consumableId ?? null,
        type: partId
          ? PurchaseItemType.part
          : consumableId
          ? PurchaseItemType.consumable
          : PurchaseItemType.task,
        purchaseItemTaxes: {
          ...values.purchaseItemTaxes,
          calcPercentPis: values.purchaseItemTaxes?.calcPercentPis || 1,
          calcPercentPisSt: values.purchaseItemTaxes?.calcPercentPisSt || 1,
          calcPercentCofins: values.purchaseItemTaxes?.calcPercentCofins || 1,
          calcPercentCofinsSt:
            values.purchaseItemTaxes?.calcPercentCofinsSt || 1,
        },
      };

      await createItemMutation.mutateAsync(obj);

      history.push(
        `/app/movements/purchases/purchasesReleases/${purchaseId}/itemsPurchase`,
      );
    } catch {}
  };

  return (
    <TabContentWrapper
      permissions={route.permissions}
      title={route.name}
      extra={
        <>
          <SaveButton
            block
            type="primary"
            onClick={handleCreateItemPurchase}
            loading={createItemMutation.isLoading}
          >
            <FormattedMessage id="commons.entity.save" />
          </SaveButton>
        </>
      }
    >
      <Helmet
        title={intl.formatMessage({
          id: 'layout.menu.movements.purchases.details.itemsPurchase.create',
        })}
      />

      <PurchaseItemsFormTabs
        form={form}
        purchase={purchase}
        isTask={!part && !consumable}
      />

      <PurchaseItemsButtonsRepeated purchase={purchase} />
    </TabContentWrapper>
  );
}

export default PurchaseCreateItemPurchase;
